import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { Dpad, RocketTakeoff, Magic } from 'react-bootstrap-icons';
import { PricingTier } from '../types/subscription.types';
import subscriptionService from '../services/subscriptionService';
import AlertBanner from '../components/AlertBanner';
import { useAuth } from '../contexts/AuthContext';
import { ENV } from '../utils/env';
import { getUtmParams } from '../utils/utmTracking';
import './SelectPlan.css';

const SelectPlan: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserProfile, currentUser, userProfile, trialStatus, logout } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(
    () => new URLSearchParams(window.location.search).get('checkout') === 'success'
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  // Check if user was redirected here due to trial expiration
  const trialExpiredRedirect = location.state?.trialExpired === true;
  // Also check URL param for API-triggered redirects
  const params = new URLSearchParams(location.search);
  const trialExpiredParam = params.get('trial_expired') === 'true';
  const isTrialExpired = trialExpiredRedirect || trialExpiredParam || trialStatus?.isTrialExpired;

  // If user is logged in and navigated here, it's an update flow
  const isUpdateFlow = !!currentUser;

  // Auto-trigger Stripe checkout if redirected from registration with a pre-selected plan
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preSelectedPlan = params.get('plan');
    if (preSelectedPlan && currentUser && (preSelectedPlan === 'pro' || preSelectedPlan === 'ultra')) {
      localStorage.removeItem('pendingPlan');
      const tier = pricingTiers.find((t) => t.name === preSelectedPlan);
      if (tier) {
        handleProcessPlan(tier);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Check for checkout result in URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      setShowSuccessBanner(true);
      // Poll for subscription update before redirecting
      // Stripe webhook may take a few seconds to update the subscription
      let pollCount = 0;
      const maxPolls = 20; // Max 20 seconds
      const pollInterval = setInterval(async () => {
        pollCount++;
        try {
          const status = await subscriptionService.getSubscriptionStatus();
          // Check if subscription is no longer on starter plan
          if (status.subscription?.planName !== 'starter') {
            clearInterval(pollInterval);
            // Refresh the user profile in AuthContext before redirecting
            const freshProfile = await refreshUserProfile();
            // Wait a bit to show success message, then redirect
            setTimeout(() => {
              // Redirect based on onboarding status, not URL param
              const redirectTo = freshProfile?.onboardingCompleted ? '/dashboard' : '/onboarding';
              navigate(redirectTo);
            }, 1500);
          } else if (pollCount >= maxPolls) {
            // Timeout - redirect anyway after 20 seconds
            clearInterval(pollInterval);
            const freshProfile = await refreshUserProfile();
            const redirectTo = freshProfile?.onboardingCompleted ? '/dashboard' : '/onboarding';
            navigate(redirectTo);
          }
        } catch (error) {
          console.error('Error checking subscription status:', error);
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            navigate('/onboarding');
          }
        }
      }, 1000); // Poll every second

      return () => clearInterval(pollInterval);
    } else if (params.get('checkout') === 'canceled') {
      setError('Checkout was canceled. Please select a plan to continue.');
      setShowErrorBanner(true);
    }
  }, [location.search, navigate, refreshUserProfile]);

  // Show error banner when error changes
  useEffect(() => {
    if (error) {
      setShowErrorBanner(true);
    }
  }, [error]);

  const pricingTiers: PricingTier[] = [
    {
      name: 'starter',
      displayName: '30 Day Trial',
      price: 0,
      priceId: '',
      interval: 'month',
      description: 'For exploring your capabilities',
      features: [
        '1 project total',
        '100 messages total',
        'Limited templates',
        '5 artifact generations total',
        '2 Figma exports total',
      ],
    },
    {
      name: 'pro',
      displayName: 'Early Adopter',
      price: 18.95,
      priceId: ENV.STRIPE_PRICE_ID_PRO || '',
      interval: 'month',
      recommended: true,
      description: 'For casual designers needing flexibility',
      features: [
        '10 projects total',
        '500 messages/month',
        'Unlimited templates',
        '50 artifact generations/month',
        '25 Figma exports/month',
        'Direct Figma plugin integration',
      ],
    },
    {
      name: 'ultra',
      displayName: 'Studio',
      price: 78.95,
      priceId: ENV.STRIPE_PRICE_ID_ULTRA || '',
      interval: 'month',
      description: 'For professionals and small teams',
      features: [
        '25 projects total',
        '1,000 messages/month',
        'Unlimited templates',
        'Unlimited artifact generations',
        'Unlimited Figma exports',
        'Early Beta feature access',
      ],
    },
  ];

  // Handle clicking on a plan card button - just selects the plan (in update flow)
  const handlePlanSelection = (tierName: string) => {
    // If already on this plan, do nothing
    if (userProfile?.subscription?.planName === tierName) {
      return;
    }
    setSelectedPlan(tierName);
  };

  // Handle plan selection in registration flow (non-update flow)
  const handleProcessPlan = async (tier: PricingTier) => {
    // Handle selecting starter (free) plan - just navigate to home
    if (tier.name === 'starter') {
      navigate('/onboarding');
      return;
    }

    // Paid plans - go through Stripe checkout
    try {
      setLoading(tier.name);
      setError(null);

      const successUrl = `${window.location.origin}/select-plan?checkout=success`;
      const cancelUrl = `${window.location.origin}/select-plan?checkout=canceled`;

      const { url } = await subscriptionService.createCheckoutSession({
        priceId: tier.priceId,
        successUrl,
        cancelUrl,
        utmParams: getUtmParams(),
      });

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err: any) {
      console.error('Error processing subscription:', err);
      setError(err.response?.data?.error || 'Failed to process subscription');
      setLoading(null);
    }
  };

  const getIcon = (tierName: string) => {
    switch (tierName) {
      case 'starter':
        return <Dpad size={24} className="plan-icon" />;
      case 'pro':
        return <RocketTakeoff size={24} className="plan-icon" />;
      case 'ultra':
        return <Magic size={24} className="plan-icon" />;
      default:
        return <Dpad size={24} className="plan-icon" />;
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // Handle Next button - navigates to confirmation page
  const handleNextClick = () => {
    if (selectedPlan) {
      navigate('/plan-confirmation', { state: { selectedPlan } });
    }
  };

  if (showSuccessBanner) {
    return (
      <div className="select-plan-page">
        <div className="activating-subscription-overlay">
          <Spinner animation="border" role="status" className="activating-spinner" />
          <p className="activating-text">Activating Subscription</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showErrorBanner && error && (
        <AlertBanner
          message={error}
          type="error"
          onClose={() => setShowErrorBanner(false)}
          autoClose={true}
          autoCloseDelay={10000}
        />
      )}
      <div className="select-plan-page">
        <Container className="select-plan-container">
{/* Back link removed - now using Back/Next buttons below the cards */}
          <div className="select-plan-header">
            <h1>{isTrialExpired ? 'subscribe to continue' : isUpdateFlow ? 'update your plan' : 'select a plan'}</h1>
            <p className="subtitle">
              {isTrialExpired
                ? 'YOUR FREE TRIAL HAS ENDED'
                : isUpdateFlow
                  ? 'UPGRADE OR CHANGE YOUR SUBSCRIPTION'
                  : 'A PLAN TO SUIT YOUR PARTICULAR NEEDS'}
            </p>
          </div>

          {isTrialExpired && (
            <AlertBanner
              message="Your 30-day free trial has ended. Subscribe to Early Adopter or Studio to continue using Revealer."
              type="warning"
              onClose={() => {}}
              autoClose={false}
            />
          )}

          <Row className="plans-row">
            {pricingTiers
              .filter((tier) => {
                // Hide Starter plan when trial has expired
                if (isTrialExpired && tier.name === 'starter') {
                  return false;
                }
                // Hide Starter plan in update flow if user is not currently on Starter
                if (isUpdateFlow && userProfile?.subscription?.planName !== 'starter' && tier.name === 'starter') {
                  return false;
                }
                return true;
              })
              .map((tier) => (
              <Col key={tier.name} className="mb-4 plan-col">
                <div className="plan-card-wrapper">
                  {tier.name === 'pro' && !(isUpdateFlow && userProfile?.subscription?.planName === 'pro') && !(isUpdateFlow && userProfile?.subscription?.planName === 'ultra') && (
                    <div className="plan-badge plan-badge-recommended">RECOMMENDED</div>
                  )}
                  {tier.name === 'ultra' && !(isUpdateFlow && userProfile?.subscription?.planName === 'ultra') && (
                    <div className="plan-badge plan-badge-limited">LIMITED TIME OFFER</div>
                  )}
                  <Card className={`plan-card ${isUpdateFlow && selectedPlan === tier.name ? 'highlighted' : ''}`}>
                    <Card.Body>
                      {getIcon(tier.name)}
                    <h2 className="plan-name">{tier.displayName}</h2>
                    <div className="plan-price">
                      {tier.name === 'starter' ? null : <span className="currency">$</span>}
                      <span className="price-amount">{tier.name === 'starter' ? 'Free' : tier.price}</span>
                      {tier.name === 'starter' ? null : <span className="price-period">/{tier.interval}</span>}
                      {tier.name === 'pro' && (
                        <span className="was-price"> was <s>$49</s></span>
                      )}
                      {tier.name === 'ultra' && (
                        <span className="was-price"> was <s>$149</s></span>
                      )}
                    </div>
                    {tier.description && (
                      <p className="plan-description">{tier.description}</p>
                    )}

                    <Button
                      variant={tier.recommended && !isUpdateFlow ? 'primary' : 'secondary'}
                      className={`select-plan-btn ${isUpdateFlow && selectedPlan === tier.name ? 'selected' : ''}`}
                      onClick={() => isUpdateFlow ? handlePlanSelection(tier.name) : handleProcessPlan(tier)}
                      disabled={
                        !!loading ||
                        showSuccessBanner ||
                        userProfile?.subscription?.planName === tier.name
                      }
                    >
                      {loading === tier.name ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Processing...
                        </>
                      ) : userProfile?.subscription?.planName === tier.name ? (
                        'CURRENT PLAN'
                      ) : isUpdateFlow && selectedPlan === tier.name ? (
                        'SELECTED'
                      ) : tier.name === 'starter' && userProfile?.subscription?.planName && userProfile.subscription.planName !== 'starter' ? (
                        'CANCEL SUBSCRIPTION'
                      ) : (
                        'SELECT PLAN'
                      )}
                    </Button>

                    <ul className="plan-features">
                      {tier.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
                </div>
              </Col>
            ))}
          </Row>

          {/* Back and Next Navigation Buttons - only show in update flow */}
          {isUpdateFlow && (
            <div className="plan-navigation-buttons">
              <Button
                variant="outline-secondary"
                className="plan-nav-btn back-btn"
                onClick={handleGoBack}
                disabled={!!loading}
              >
                Back
              </Button>
              <Button
                variant="primary"
                className="plan-nav-btn next-btn"
                onClick={handleNextClick}
                disabled={!selectedPlan || !!loading}
              >
                Continue
              </Button>
            </div>
          )}

          {/* Logout button - only show when trial has expired */}
          {isTrialExpired && currentUser && (
            <div className="plan-logout-container">
              <Button
                variant="link"
                className="plan-logout-btn"
                onClick={() => logout().then(() => navigate('/'))}
                disabled={!!loading}
              >
                Log out
              </Button>
            </div>
          )}

          {/* Activity Spinner Overlay */}
          {loading && (
            <div className="plan-loading-overlay">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Processing...</span>
              </Spinner>
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default SelectPlan;
